'use client';

import { ArrowUpIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function ScrollToTop({
  className,
  ...props
}: React.ComponentProps<'button'>) {
  const [visible, setVisible] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const prevScrollYRef = useRef(0);

  useEffect(() => {
    let rafId = 0;

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const y = window.scrollY;
        setVisible(y >= 400);
        setScrollDirection(y > prevScrollYRef.current ? 'down' : 'up');
        prevScrollYRef.current = y;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <Button
      data-visible={visible}
      data-scroll-direction={scrollDirection}
      inert={!visible}
      aria-hidden={!visible}
      tabIndex={visible ? undefined : -1}
      className={cn(
        '[--bottom:1rem] lg:[--bottom:2rem]',
        // Left side; the chat dock owns the bottom-right corner.
        'fixed left-4 bottom-[calc(var(--bottom,1rem)+env(safe-area-inset-bottom,0px))] z-50 print:hidden lg:left-8',
        'transition-[background-color,opacity] duration-300 data-[scroll-direction=down]:opacity-30 data-[scroll-direction=up]:opacity-100 data-[visible=false]:pointer-events-none data-[visible=false]:opacity-0',
        'data-[scroll-direction=down]:hover:opacity-100',
        className
      )}
      variant="secondary"
      size="icon-lg"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      {...props}
    >
      <ArrowUpIcon className="size-5" />
      <span className="sr-only">Scroll to top</span>
    </Button>
  );
}
